import { useEffect } from 'react';
import { getIOSVersion, isIOS } from '../Utils/platforms';

/**
 * Detects the iOS Safari "Review permissions" banner that appears when a user
 * toggles on a Safari extension.
 *
 * iPhone — pre-iOS 18 / iOS 26+:
 *   - The banner reduces `window.innerHeight`. Polls every 500ms and redirects
 *     on any drop >20px (skipping while the user is actively scrolling).
 *
 * iPhone — iOS 18:
 *   - The native extensions menu (AA → manage extensions) ALSO causes a height
 *     drop, so the simple "drop = banner" rule produces false positives.
 *   - Empirical signature (from on-device logs):
 *       - Menu open:      innerHeight ≈ baseline − 80px (large DROP)
 *       - Banner showing: innerHeight ≈ baseline + 6px  (small INCREASE — Safari
 *                         hides the bottom toolbar to make room for the banner)
 *   - We rely on the `window.resize` event (fires only when layout settles) and
 *     redirect only when innerHeight ends up ABOVE baseline.
 *
 * iPad (all iOS versions):
 *   - The banner produces a small settled height DROP
 *     (~10–20px). Use `resize` with a small threshold.
 *
 * @param redirectPath - The path to navigate to when the banner is detected.
 */
const useReviewBannerDetection = (redirectPath: string) => {
  useEffect(() => {
    // Compute iPad synchronously from the UA so the correct branch is taken on
    // the first effect run. `useIsIPad()` would be `false` on the initial render
    // (it only sets state inside its own effect), causing iPadOS 13+ — which
    // reports `isIOS === false` — to either early-return or take the iPhone
    // path and miss the one-time banner resize event.
    const ua = navigator.userAgent || navigator.vendor;
    const isIPadNow =
      /iPad/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document);

    if (!isIOS && !isIPadNow) return;

    const isIOS18Phone = !isIPadNow && getIOSVersion() === 18;

    let baselineHeight = window.innerHeight;
    let isScrolling = false;
    let scrollCooldown: ReturnType<typeof setTimeout>;

    const onTouchMove = () => {
      isScrolling = true;
      clearTimeout(scrollCooldown);
    };

    const onTouchEnd = () => {
      clearTimeout(scrollCooldown);
      scrollCooldown = setTimeout(() => {
        baselineHeight = window.innerHeight;
        isScrolling = false;
      }, 2000);
    };

    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });

    let onResize: (() => void) | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;

    if (isIOS18Phone) {
      // iOS 18 iPhone: banner appearing causes a small height INCREASE (Safari
      // hides its bottom toolbar). The native extensions menu causes a DROP,
      // which we ignore. Listen to `resize` (settled-state only).
      onResize = () => {
        const h = window.innerHeight;
        const diff = h - baselineHeight;

        // Re-baseline on large changes (e.g. orientation)
        if (Math.abs(diff) > 100) {
          baselineHeight = h;
          return;
        }

        if (!isScrolling && diff >= 3) {
          window.location.href = redirectPath + window.location.search;
        }
      };
      window.addEventListener('resize', onResize);
    } else if (isIPadNow) {
      // iPad: banner causes a small height DROP (no bottom toolbar to hide,
      // and the extensions menu on iPad is a popover that doesn't resize the
      // viewport — so any settled drop is the banner). Use `resize` for the
      // settled value and a small threshold since the change is only ~10–20px.
      onResize = () => {
        const h = window.innerHeight;
        const diff = h - baselineHeight;

        // Re-baseline on large changes (e.g. orientation)
        if (Math.abs(diff) > 100) {
          baselineHeight = h;
          return;
        }

        if (!isScrolling && diff <= -3) {
          window.location.href = redirectPath + window.location.search;
        }
      };
      window.addEventListener('resize', onResize);
    } else {
      // Pre-iOS 18 / iOS 26+ iPhone: banner reduces innerHeight; redirect on
      // any settled drop >20px via 500ms polling.
      onResize = () => {
        const h = window.innerHeight;
        const diff = h - baselineHeight;
        // Re-baseline on large changes only (e.g. orientation change)
        if (Math.abs(diff) > 100) {
          baselineHeight = h;
        }
      };
      window.addEventListener('resize', onResize);

      interval = setInterval(() => {
        const drop = baselineHeight - window.innerHeight;
        if (!isScrolling && drop > 20) {
          if (interval) clearInterval(interval);
          window.location.href = redirectPath + window.location.search;
        }
      }, 500);
    }

    return () => {
      if (interval) clearInterval(interval);
      clearTimeout(scrollCooldown);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      if (onResize) window.removeEventListener('resize', onResize);
    };
  }, [redirectPath]);
};

export default useReviewBannerDetection;
